import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import VideoControlsBar from '../video-controls-bar/video-controls-bar';
import Loader from '../loader/loader';
import Hotspot from '../silvr/Hotspot';
import ShopChip from '../silvr/ShopChip';
import ProductPreview from '../silvr/ProductPreview';
import ProductDrawer from '../silvr/ProductDrawer';
import type { ShoppableProduct, StoreItem } from '../silvr/types';
import { PublishedClip, PublishedProduct, PublishedTrack, shortCategory, storeSearchUrl, trackedUrl, trackPositionAt } from '../../utils/clipApi';
import '../silvr/silvr.scss';
import './shoppable-video.scss';

// "Shop this scene" shows a spinner for a moment, then reveals the hotspots. Kept from the previous
// demo: it reads as "recognising the scene", and it gives the seek a beat to settle.
const REVEAL_DELAY_MS = 700;

/** The product as the drawer renders it. Every listing link goes through the affiliate redirect here
 *  (trackedUrl), so a link typed into a product file cannot bypass it; the web-search fallback for a
 *  catalog match without a listing stays plain, it is not a listing. */
export function toStoreItem(product: PublishedProduct, id: string, source: string): StoreItem {
  const links = product.links?.length ? product.links.map((link) => ({ ...link, url: trackedUrl(link.url, source) })) : undefined;
  const direct = product.url ? trackedUrl(product.url, source) : links?.[0]?.url;
  return {
    id,
    name: product.name || 'Catalog item',
    brand: product.brand || product.merchant || 'Unknown brand',
    price: product.price || 'Check retailer',
    image: product.image_url,
    url: direct || storeSearchUrl(product),
    links,
    category: shortCategory(product.category),
    color: product.color,
    matchType: product.match_type,
  };
}

export function toShoppable(track: PublishedTrack, position: { x: number; y: number }, source: string): ShoppableProduct {
  return {
    ...toStoreItem(track.product, `t${track.id}`, source),
    x: position.x,
    y: position.y,
    similar: track.alternates.map((alt) => toStoreItem(alt, `t${track.id}-a${alt.gallery_row}`, source)),
  };
}

/** The rectangle the video's pixels actually occupy inside the element (object-fit: contain). */
export function contentRect(element: HTMLVideoElement) {
  const { clientWidth: w, clientHeight: h, videoWidth, videoHeight } = element;
  if (!w || !h || !videoWidth || !videoHeight) return { left: 0, top: 0, width: w, height: h };
  const scale = Math.min(w / videoWidth, h / videoHeight);
  const width = videoWidth * scale;
  const height = videoHeight * scale;
  return { left: (w - width) / 2, top: (h - height) / 2, width, height };
}

interface ShoppableVideoProps {
  clip: PublishedClip;
  /** The `source` on every tracked listing link: one value per demo (e.g. "demo-pll"). */
  clickSource: string;
}

/**
 * The clip with Silvr's shopping surface. The sequence (Jason, 2026-10-22):
 *   1. landing: the clip and its controls, nothing else on the picture;
 *   2. pause: a "Shop this scene" chip in the top-left corner (clear of the controls bar), and only that;
 *   3. the chip: a beat of "recognising", then a "+" on every published garment in the frame;
 *      a "+" opens the product, the chip (now active) opens the whole look.
 * Play clears all of it.
 */
const ShoppableVideo: React.FC<ShoppableVideoProps> = ({ clip, clickSource }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(true);
  // False until the viewer presses play for the first time: a paused clip on landing is not a
  // paused scene, so no chip and no hotspots sit on top of it.
  const [started, setStarted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // 'off' (nothing on the picture) -> 'recognising' (spinner, after the chip) -> 'on' (hotspots).
  const [hotspots, setHotspots] = useState<'off' | 'recognising' | 'on'>('off');
  const [frame, setFrame] = useState(0);
  const [overlay, setOverlay] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [preview, setPreview] = useState<ShoppableProduct | null>(null);
  const [previewPosition, setPreviewPosition] = useState<{ left: number; top: number } | null>(null);
  const [selected, setSelected] = useState<ShoppableProduct | StoreItem | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [similarTarget, setSimilarTarget] = useState<ShoppableProduct | null>(null);
  const revealTimer = useRef<number | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewAnchorRef = useRef<HTMLElement | null>(null);

  const fps = clip.source.fps || 24;

  // --- what is on screen right now
  const products = useMemo(() => {
    const out: ShoppableProduct[] = [];
    for (const track of clip.tracks) {
      const position = trackPositionAt(track, frame);
      if (!position) continue;
      out.push(toShoppable(track, position, clickSource));
    }
    return out;
  }, [clip, frame, clickSource]);
  // Two garments can resolve to the same catalog product (a dress and the cardigan over it, say);
  // the list shows each product once while every hotspot stays.
  const listProducts = useMemo(() => {
    const seen = new Set<string>();
    return products.filter((product) => {
      const key = `${product.brand}|${product.name}|${product.image}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [products]);

  // --- video plumbing
  const syncFrame = useCallback(() => {
    const video = videoRef.current;
    if (video) setFrame(Math.round(video.currentTime * fps));
  }, [fps]);

  const syncOverlay = useCallback(() => {
    const video = videoRef.current;
    if (video) setOverlay(contentRect(video));
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const resize = new ResizeObserver(syncOverlay);
    resize.observe(video);
    video.addEventListener('loadedmetadata', syncOverlay);
    video.addEventListener('timeupdate', syncFrame);
    video.addEventListener('seeked', syncFrame);
    return () => {
      resize.disconnect();
      video.removeEventListener('loadedmetadata', syncOverlay);
      video.removeEventListener('timeupdate', syncFrame);
      video.removeEventListener('seeked', syncFrame);
    };
  }, [syncFrame, syncOverlay]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const clearLeave = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
  };
  const hidePreview = () => {
    setPreview(null);
    setPreviewPosition(null);
  };
  const scheduleLeave = () => {
    clearLeave();
    leaveTimer.current = setTimeout(hidePreview, 160);
  };
  const clearReveal = () => {
    if (revealTimer.current) window.clearTimeout(revealTimer.current);
    revealTimer.current = null;
  };
  useEffect(
    () => () => {
      clearLeave();
      clearReveal();
    },
    [],
  );

  /** The chip was pressed: a beat of "recognising", then the hotspots. */
  const revealHotspots = () => {
    clearReveal();
    setHotspots('recognising');
    revealTimer.current = window.setTimeout(() => setHotspots('on'), REVEAL_DELAY_MS);
  };

  const handlePlay = () => {
    clearReveal();
    setStarted(true);
    setIsPaused(false);
    setHotspots('off');
    hidePreview();
    setSheetOpen(false);
    setSimilarTarget(null);
    setSelected(null);
  };

  const handlePause = () => {
    const video = videoRef.current;
    if (!video || document.pictureInPictureElement) return;
    syncFrame();
    setIsPaused(true);
  };

  const togglePlayback = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('.video-controls-bar, [data-silvr-ui], .silvr-ui')) return;
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  // --- preview placement: next to the hotspot, inside the viewport and above the controls bar
  // (Josh, 2026-10-22: the card for a "+" low in the frame sat over the scrubber; it now ends above it).
  const placePreview = (anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect();
    const cardWidth = Math.min(440, window.innerWidth - 32);
    // The card as rendered once it is on screen (see the layout effect below); an estimate before that.
    const card = document.querySelector<HTMLElement>('.silvr-preview-overlay .product-preview');
    const cardHeight = card?.offsetHeight || 220;
    const gap = 18;
    const narrow = window.innerWidth <= 600;
    const bar = containerRef.current?.querySelector('.video-controls-bar');
    const floor = Math.min(window.innerHeight - 16, bar ? bar.getBoundingClientRect().top - 8 : Infinity);
    const preferredLeft = rect.right + gap + cardWidth <= window.innerWidth - 16 ? rect.right + gap : rect.left - gap - cardWidth;
    const left = narrow ? 16 : Math.max(16, Math.min(preferredLeft, window.innerWidth - cardWidth - 16));
    const preferredTop = narrow
      ? rect.bottom + gap + cardHeight <= floor
        ? rect.bottom + gap
        : rect.top - gap - cardHeight
      : rect.top + rect.height / 2 - 44;
    const top = Math.max(16, Math.min(preferredTop, floor - cardHeight));
    setPreviewPosition({ left, top });
  };

  const showPreview = (product: ShoppableProduct, anchor: HTMLElement) => {
    clearLeave();
    previewAnchorRef.current = anchor;
    setPreview(product);
    placePreview(anchor);
  };

  // Place the card again once it has rendered, now with its real height: a card pushed up off the
  // controls bar sits right above it, not an estimate's distance away.
  useLayoutEffect(() => {
    if (preview && previewAnchorRef.current?.isConnected) placePreview(previewAnchorRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  useEffect(() => {
    if (!previewPosition) return undefined;
    const sync = () => {
      const anchor = previewAnchorRef.current;
      if (!anchor?.isConnected) {
        hidePreview();
        return;
      }
      placePreview(anchor);
    };
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(previewPosition)]);

  const openItem = (product: ShoppableProduct) => {
    clearLeave();
    hidePreview();
    setSelected(product);
    setSheetOpen(true);
  };
  const openList = () => {
    hidePreview();
    setSelected(null);
    setSimilarTarget(null);
    setSheetOpen(true);
  };
  const closeSheet = () => {
    setSheetOpen(false);
    setSimilarTarget(null);
    setSelected(null);
  };

  // A paused scene (not the landing frame) carries the chip; the hotspots come after the chip.
  const sceneMode = started && isPaused;
  const shopMode = sceneMode && hotspots === 'on';

  return (
    <div className={`shoppable-video ${isFullscreen ? 'is-fullscreen' : ''}`} ref={containerRef} onClick={togglePlayback}>
      <video
        id="player"
        ref={videoRef}
        src={clip.video_url}
        playsInline
        preload="metadata"
        onLoadedMetadata={syncFrame}
        onPlay={handlePlay}
        onPause={handlePause}
        onSeeked={syncFrame}
        onEnded={handlePause}
        className={shopMode ? 'is-shopping' : ''}
      />
      {/* Everything positioned over the video is laid out inside the video's own pixel rectangle,
          so a hotspot at (x%, y%) lands on the garment whatever the letterboxing. */}
      <div className="shoppable-video__frame" style={overlay}>
        {sceneMode && hotspots === 'recognising' && (
          <div className="shoppable-video__recognising" aria-live="polite" aria-label="Finding products in this scene">
            <Loader />
          </div>
        )}
        {shopMode &&
          products.map((product) => (
            <Hotspot key={product.id} product={product} isActive={preview?.id === product.id} onHover={showPreview} onLeave={scheduleLeave} onOpen={openItem} />
          ))}
        {/* One chip in the corner, whatever the scene holds: "Shop this scene", no count (Jason,
            SIL-1592); two chips used to stack here on an empty frame. On an empty scene the list
            says there is nothing. */}
        {sceneMode && hotspots !== 'recognising' && (
          <ShopChip label="Shop this scene" video corner="top-left" onClick={shopMode ? openList : revealHotspots} />
        )}
      </div>
      {preview &&
        !sheetOpen &&
        createPortal(
          <div className={`silvr-preview-overlay ${previewPosition ? 'is-anchored' : ''}`} aria-live="polite">
            <ProductPreview
              product={preview}
              position={previewPosition ?? undefined}
              onEnter={clearLeave}
              onLeave={scheduleLeave}
              onOpen={() => openItem(preview)}
              onSimilar={() => {
                setSelected(preview);
                setSimilarTarget(preview);
                setSheetOpen(true);
                hidePreview();
              }}
            />
          </div>,
          document.body,
        )}
      <VideoControlsBar videoRef={videoRef} isPaused={isPaused} isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />
      <ProductDrawer
        isOpen={sheetOpen}
        onClose={closeSheet}
        products={listProducts}
        selectedProduct={selected}
        onSelectProduct={setSelected}
        similarTarget={similarTarget}
        onShowSimilar={setSimilarTarget}
        onClearSimilar={() => setSimilarTarget(null)}
      />
    </div>
  );
};

export default ShoppableVideo;
